import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum } from "@/lib/format";
import { attT } from "@/lib/attendanceI18n";
import { exportTimesheetPdf } from "@/lib/timesheetPdf";

// Compact color per status — used as cell background (KTZ-style).
// We render the actual check-in/check-out times INSIDE the cell now,
// rather than just a glyph. Glyph is kept as a fallback (no times).
const STATUS_VIZ = {
  present:        { sym: "✓",  bg: "#16a34a", color: "#fff", label: "حاضر" },
  half:           { sym: "½",  bg: "#65a30d", color: "#fff", label: "نصف دوام" },
  absent:         { sym: "✗",  bg: "#dc2626", color: "#fff", label: "غائب" },
  excused:        { sym: "⊘",  bg: "#2563eb", color: "#fff", label: "بعذر" },
  approved_leave: { sym: "📋", bg: "#7c3aed", color: "#fff", label: "إجازة" },
  permitted:      { sym: "⚑",  bg: "#0891b2", color: "#fff", label: "مأذون" },
  sick:           { sym: "✚",  bg: "#0d9488", color: "#fff", label: "مرضي" },
  off:            { sym: "—",  bg: "#475569", color: "#cbd5e1", label: "إجازة رسمية" },
  unrecorded:     { sym: "·",  bg: "transparent", color: "#94a3b8", label: "لم يُسجّل" },
};

// Format ISO → "HH:MM" in Asia/Riyadh (UTC+3, no DST).
function formatRiyadhTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const utcMs = d.getTime() + d.getTimezoneOffset() * 60_000;
    const local = new Date(utcMs + 3 * 60 * 60_000);
    return `${String(local.getHours()).padStart(2, "0")}:${String(local.getMinutes()).padStart(2, "0")}`;
  } catch {
    return null;
  }
}

// Compute worked minutes for a single cell (check_in → check_out, capped at 16h).
function workedMinutes(c) {
  if (!c?.check_in_at || !c?.check_out_at) return 0;
  try {
    const a = new Date(c.check_in_at).getTime();
    const b = new Date(c.check_out_at).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
    const m = Math.round((b - a) / 60000);
    return Math.max(0, Math.min(m, 16 * 60));
  } catch {
    return 0;
  }
}

// Parse "YYYY-MM-DD" → Date at local midnight.
function parseISODate(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map((x) => parseInt(x, 10));
  return new Date(y, (m || 1) - 1, d || 1);
}

export default function TimesheetTab({ canEdit }) {
  const { theme, lang, dir } = useI18n();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);

  // Range filter: "month" (default) or "custom".
  const [rangeMode, setRangeMode] = useState("month");
  const isoToday = today.toISOString().slice(0, 10);
  const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  const [rangeFrom, setRangeFrom] = useState(firstOfMonth);
  const [rangeTo, setRangeTo] = useState(isoToday);

  const MONTHS = attT(lang, "months");
  const DOW = attT(lang, "dow.short");

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/admin/attendance/timesheet?year=${year}&month=${month}`);
      setData(data);
    } catch (e) { setErr(formatApiError(e)); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  // Filter rows by name + apply custom date range (subset of the loaded month).
  const filteredRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    let rows = data.rows;
    if (q) {
      rows = rows.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.username || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, search]);

  // Days to display: full month OR clipped to custom range (within same month).
  const visibleDays = useMemo(() => {
    if (!data) return [];
    if (rangeMode === "month") return data.days;
    const from = parseISODate(rangeFrom);
    const to = parseISODate(rangeTo);
    if (!from || !to) return data.days;
    return data.days.filter((d) => {
      const dt = new Date(year, month - 1, d);
      return dt >= from && dt <= to;
    });
  }, [data, rangeMode, rangeFrom, rangeTo, year, month]);

  // Recompute KPIs across visible days only (so custom range stats are accurate).
  const kpis = useMemo(() => {
    if (!data) return { present: 0, absent: 0, lateCount: 0, lateMinutes: 0, workedMinutes: 0 };
    let present = 0, absent = 0, lateCount = 0, lateMinutes = 0, worked = 0;
    for (const r of filteredRows) {
      for (const d of visibleDays) {
        const c = r.cells[d];
        if (!c) continue;
        if (c.status === "present" || c.status === "half") present += 1;
        if (c.status === "absent") absent += 1;
        if ((c.minutes_late || 0) > 0) { lateCount += 1; lateMinutes += c.minutes_late; }
        worked += workedMinutes(c);
      }
    }
    return { present, absent, lateCount, lateMinutes, workedMinutes: worked };
  }, [data, filteredRows, visibleDays]);

  const onExportPdf = async () => {
    if (!data) return;
    try {
      await exportTimesheetPdf({
        lang, year, month,
        rows: filteredRows,
        days: visibleDays,
        statusViz: STATUS_VIZ,
        monthName: MONTHS[month - 1],
        kpis,
      });
    } catch (e) {
      setErr("PDF: " + (e?.message || e));
    }
  };

  const css = makeStyles(theme, dir);

  if (!data) return <div style={{ padding: 40, color: theme.textMuted }}>{err || attT(lang, "loading")}</div>;

  return (
    <div data-testid="attendance-timesheet-tab">
      {err && <div style={css.err}>{err}</div>}

      {/* Toolbar */}
      <div style={css.toolbar}>
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={attT(lang, "ts.search_ph")} style={css.search} data-testid="timesheet-search" />
        <select value={rangeMode} onChange={(e) => setRangeMode(e.target.value)} style={css.select} data-testid="timesheet-range-mode">
          <option value="month">{attT(lang, "ts.range.month_mode")}</option>
          <option value="custom">{attT(lang, "ts.range.custom_mode")}</option>
        </select>
        {rangeMode === "month" ? (
          <>
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} style={css.select} data-testid="timesheet-month">
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} style={css.select} data-testid="timesheet-year">
              {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{String(y)}</option>)}
            </select>
          </>
        ) : (
          <>
            <label style={css.label}>{attT(lang, "ts.range.from")}</label>
            <input type="date" value={rangeFrom} onChange={(e) => {
              setRangeFrom(e.target.value);
              const d = parseISODate(e.target.value);
              if (d) { setYear(d.getFullYear()); setMonth(d.getMonth() + 1); }
            }} style={css.select} data-testid="timesheet-range-from" />
            <label style={css.label}>{attT(lang, "ts.range.to")}</label>
            <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} style={css.select} data-testid="timesheet-range-to" />
          </>
        )}
        <button onClick={onExportPdf} style={css.btn} data-testid="timesheet-pdf">{attT(lang, "ts.export_pdf")}</button>
      </div>

      {/* KPI cards */}
      <KpiCards lang={lang} theme={theme} kpis={kpis} />

      <Legend lang={lang} />

      <div style={css.scrollX}>
        <table style={css.table}>
          <thead>
            <tr>
              <th style={{ ...css.th, ...css.thName }}>{attT(lang, "ts.col.employee")}</th>
              <th style={{ ...css.th, ...css.thShift }}>{attT(lang, "ts.col.shift")}</th>
              {visibleDays.map((d) => {
                const dt = new Date(year, month - 1, d);
                const dow = dt.getDay(); // 0=Sun..6=Sat
                const isFri = dow === 5;
                return (
                  <th key={d} style={{ ...css.th, ...(isFri ? css.thFri : {}) }} title={dt.toDateString()}>
                    <div style={{ fontSize: 10, color: theme.textMuted }}>{DOW[dow]}</div>
                    <div>{fmtNum(d)}</div>
                  </th>
                );
              })}
              <th style={{ ...css.th, ...css.thTotal }}>{attT(lang, "ts.col.present")}</th>
              <th style={{ ...css.th, ...css.thTotal }}>{attT(lang, "ts.col.absent")}</th>
              <th style={{ ...css.th, ...css.thTotal }}>{attT(lang, "ts.col.late")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={visibleDays.length + 5} style={css.empty}>{attT(lang, "ts.empty")}</td></tr>
            ) : (
              filteredRows.map((r) => (
                <tr key={r.user_id}>
                  <td style={css.tdName} data-testid={`ts-row-${r.username}`}>
                    <div style={{ fontWeight: 800 }}>{r.name}</div>
                    <div style={{ fontSize: 10, color: theme.textMuted }}>@{r.username}</div>
                  </td>
                  <td style={css.tdShift}>{attT(lang, r.shift === "evening" ? "ts.shift.evening" : "ts.shift.morning")}</td>
                  {visibleDays.map((d) => {
                    const c = r.cells[d];
                    const v = STATUS_VIZ[c?.status] || STATUS_VIZ.unrecorded;
                    const isLate = (c?.minutes_late || 0) > 0;
                    const inT = formatRiyadhTime(c?.check_in_at);
                    const outT = formatRiyadhTime(c?.check_out_at);
                    const tip = [
                      attT(lang, `status.${c?.status || "unrecorded"}`),
                      inT ? `${attT(lang, "ts.cell.in")}: ${inT}` : "",
                      outT ? `${attT(lang, "ts.cell.out")}: ${outT}` : "",
                      isLate ? attT(lang, "ts.late_minutes", { min: fmtNum(c.minutes_late) }) : "",
                      c?.note || "",
                      c?.is_ramadan ? attT(lang, "ts.legend.ramadan") : "",
                    ].filter(Boolean).join(" • ");
                    const hasTimes = inT || outT;
                    return (
                      <td
                        key={d}
                        onClick={() => canEdit && c?.status !== "off" && setEditing({ userId: r.user_id, day: d, cell: c, userName: r.name })}
                        style={{
                          ...css.tdCell,
                          background: v.bg,
                          color: v.color,
                          cursor: canEdit && c?.status !== "off" ? "pointer" : "default",
                          position: "relative",
                          opacity: c?.is_ramadan ? 0.95 : 1,
                          outline: c?.is_ramadan ? "1.5px dashed #facc15" : "none",
                          padding: hasTimes ? "4px 2px" : "0",
                        }}
                        title={tip}
                        data-testid={`ts-cell-${r.username}-${d}`}
                      >
                        {hasTimes ? (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.15 }}>
                            {inT && <span style={css.timeIn} data-testid={`ts-in-${r.username}-${d}`}>● {inT}</span>}
                            {outT && <span style={css.timeOut} data-testid={`ts-out-${r.username}-${d}`}>● {outT}</span>}
                          </div>
                        ) : (
                          <span>{v.sym}</span>
                        )}
                        {isLate && <span style={css.lateDot}>!</span>}
                      </td>
                    );
                  })}
                  <td style={css.tdTotal}>{fmtNum(r.totals.present + r.totals.half)}</td>
                  <td style={{ ...css.tdTotal, color: r.totals.absent ? "#ef4444" : theme.textMuted }}>{fmtNum(r.totals.absent)}</td>
                  <td style={{ ...css.tdTotal, color: r.totals.late_count ? "#f59e0b" : theme.textMuted }}>{fmtNum(r.totals.late_count)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditCellModal
          {...editing} year={year} month={month} lang={lang}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────── KPI cards ───────────────────────
function KpiCards({ lang, theme, kpis }) {
  const hours = Math.floor(kpis.workedMinutes / 60);
  const mins = kpis.workedMinutes % 60;
  const items = [
    { key: "hours",    testid: "kpi-hours",        title: attT(lang, "ts.kpi.hours_total"),  value: `${fmtNum(hours)} ${attT(lang, "ts.kpi.hours")} ${fmtNum(mins)} ${attT(lang, "ts.kpi.minutes")}`, color: "#16a34a" },
    { key: "present",  testid: "kpi-days-present", title: attT(lang, "ts.kpi.days_present"), value: `${fmtNum(kpis.present)} ${attT(lang, "ts.kpi.day")}`, color: "#0891b2" },
    { key: "absent",   testid: "kpi-days-absent",  title: attT(lang, "ts.kpi.days_absent"),  value: `${fmtNum(kpis.absent)} ${attT(lang, "ts.kpi.day")}`, color: "#dc2626" },
    { key: "lateCt",   testid: "kpi-late-count",   title: attT(lang, "ts.kpi.late_count"),   value: `${fmtNum(kpis.lateCount)} ${attT(lang, "ts.kpi.time")}`, color: "#f59e0b" },
    { key: "lateMin",  testid: "kpi-late-minutes", title: attT(lang, "ts.kpi.late_minutes"), value: `${fmtNum(kpis.lateMinutes)} ${attT(lang, "ts.kpi.minutes")}`, color: "#f97316" },
  ];
  return (
    <div data-testid="timesheet-kpis" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
      {items.map((it) => (
        <div key={it.key} data-testid={it.testid} style={{
          background: theme.surfaceCard,
          border: `1px solid ${theme.border}`,
          borderInlineStart: `4px solid ${it.color}`,
          borderRadius: 10,
          padding: "10px 14px",
        }}>
          <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>{it.title}</div>
          <div style={{ fontSize: 18, fontWeight: 900, color: theme.text }}>{it.value}</div>
        </div>
      ))}
    </div>
  );
}

function Legend({ lang }) {
  const items = ["present", "half", "absent", "excused", "approved_leave", "permitted", "sick", "off", "unrecorded"];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, fontSize: 11 }}>
      {items.map((key) => {
        const v = STATUS_VIZ[key];
        return (
          <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 999, background: v.bg, color: v.color, border: "1px solid rgba(255,255,255,0.1)" }}>
            <span style={{ fontWeight: 800 }}>{v.sym}</span> {attT(lang, `status.${key}`)}
          </span>
        );
      })}
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 999, outline: "1.5px dashed #facc15", color: "#facc15" }}>{attT(lang, "ts.legend.ramadan")}</span>
    </div>
  );
}

function EditCellModal({ userId, day, cell, userName, year, month, lang, onClose, onSaved }) {
  const { theme } = useI18n();
  const [status, setStatus] = useState(cell?.status || "present");
  const [note, setNote] = useState(cell?.note || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const d = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      await api.patch(`/admin/attendance/daily/${userId}/${d}`, { status, note });
      await onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={modalCss(theme).modal} onClick={(e) => e.stopPropagation()} data-testid="ts-edit-modal">
        <div style={{ fontSize: 16, fontWeight: 800, color: theme.gold, marginBottom: 12 }}>
          {attT(lang, "ts.edit.title", { name: userName, day: fmtNum(day) })}
        </div>
        {err && <div style={{ background: theme.errorBg, color: theme.errorText, padding: 8, borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{err}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: theme.textMuted }}>{attT(lang, "ts.edit.status")}</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={modalCss(theme).input} data-testid="ts-edit-status">
            {["present", "absent", "excused", "permitted", "approved_leave"].map((k) => (
              <option key={k} value={k}>{attT(lang, `status.${k}`)}</option>
            ))}
          </select>
          <label style={{ fontSize: 12, color: theme.textMuted, marginTop: 6 }}>{attT(lang, "ts.edit.note")}</label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} style={modalCss(theme).input} data-testid="ts-edit-note" placeholder={attT(lang, "ts.edit.note_ph")} />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={modalCss(theme).btn}>{attT(lang, "cancel")}</button>
          <button onClick={save} disabled={busy} style={modalCss(theme).btnPrimary} data-testid="ts-edit-save">{busy ? attT(lang, "saving") : attT(lang, "save")}</button>
        </div>
      </div>
    </div>
  );
}

const modalCss = (theme) => ({
  modal: { background: theme?.surface || "#1a1a1a", color: theme?.text || "#fff", padding: 20, borderRadius: 14, minWidth: 320, maxWidth: 420, border: `1px solid ${theme?.borderAccent || "#d4af37"}` },
  input: { background: theme?.surfaceCard || "#222", color: theme?.text || "#fff", border: `1px solid ${theme?.border || "#333"}`, borderRadius: 8, padding: "8px 10px", fontSize: 14, fontFamily: "inherit" },
  btn: { background: "transparent", color: theme?.text, border: `1px solid ${theme?.border}`, padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700 },
  btnPrimary: { background: theme?.gold || "#d4af37", color: "#000", border: "none", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontWeight: 800 },
});

const makeStyles = (theme) => ({
  err: { background: theme.errorBg, color: theme.errorText, padding: 10, borderRadius: 8, marginBottom: 10 },
  toolbar: { display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" },
  search: { flex: "1 1 220px", background: theme.surface, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 14, fontFamily: "inherit" },
  select: { background: theme.surface, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 14, fontFamily: "inherit" },
  label: { fontSize: 11, color: theme.textMuted, fontWeight: 700 },
  btn: { background: theme.gold, color: "#000", border: "none", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 800 },
  scrollX: { overflowX: "auto", border: `1px solid ${theme.border}`, borderRadius: 12 },
  table: { borderCollapse: "collapse", width: "100%", fontSize: 11 },
  th: { background: theme.surfaceCard, color: theme.text, padding: "8px 4px", textAlign: "center", borderBottom: `1px solid ${theme.border}`, position: "sticky", top: 0, zIndex: 2 },
  thName: { textAlign: "right", padding: "8px 12px", minWidth: 130, position: "sticky", insetInlineStart: 0, zIndex: 3, background: theme.surfaceCard },
  thShift: { minWidth: 60 },
  thFri: { background: "rgba(245,158,11,0.18)" },
  thTotal: { minWidth: 60, background: theme.surfaceAlt },
  tdName: { padding: "8px 12px", borderBottom: `1px solid ${theme.borderSoft}`, position: "sticky", insetInlineStart: 0, background: theme.surface, zIndex: 1 },
  tdShift: { padding: "8px 4px", borderBottom: `1px solid ${theme.borderSoft}`, textAlign: "center", fontSize: 11, color: theme.textMuted },
  tdCell: { minWidth: 56, height: 44, borderBottom: `1px solid ${theme.borderSoft}`, borderInlineStart: `1px solid ${theme.borderSoft}`, textAlign: "center", fontWeight: 700, fontSize: 11 },
  timeIn: { fontSize: 10, fontWeight: 800, color: "#bbf7d0", letterSpacing: 0.2 },
  timeOut: { fontSize: 10, fontWeight: 800, color: "#bfdbfe", letterSpacing: 0.2 },
  tdTotal: { padding: "8px 4px", borderBottom: `1px solid ${theme.borderSoft}`, textAlign: "center", fontWeight: 800, background: theme.surfaceAlt },
  empty: { textAlign: "center", padding: 30, color: theme.textMuted },
  lateDot: { position: "absolute", top: 1, insetInlineEnd: 2, fontSize: 10, color: "#fde047", fontWeight: 900 },
});
